import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import SellerDashboardLayout from '../../components/SellerDashboardLayout';
import { favoritesApi } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import { getPlanConfig } from '../../utils/membershipUtils';
import Pagination from '../../components/Pagination/Pagination';
import './FavoritesSupplierPage.css';

const FavoritesSupplierPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  // eslint-disable-next-line no-unused-vars
  const [totalItems, setTotalItems] = useState(0);
  const [favoriteSuppliers, setFavoriteSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const itemsPerPage = 16;

  // Fetch favorite suppliers
  const fetchFavorites = useCallback(async () => {
    console.log('FavoritesSupplierPage: user =', user);
    console.log('FavoritesSupplierPage: user.id =', user?.id);
    
    if (!user?.id) {
      console.log('FavoritesSupplierPage: No user ID, stopping');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      console.log('FavoritesSupplierPage: Fetching favorites for user', user.id);
      const response = await favoritesApi.getByUserId(user.id, {
        page: currentPage,
        limit: itemsPerPage
      });
      console.log('FavoritesSupplierPage: API response =', response);
      
      setFavoriteSuppliers(response.favorites || []);
      // Debug: log the first supplier's image data
      if (response.favorites && response.favorites.length > 0) {
        console.log('First supplier coverImage:', response.favorites[0].coverImage ? response.favorites[0].coverImage.substring(0, 100) + '...' : 'null');
        console.log('First supplier logo:', response.favorites[0].logo ? response.favorites[0].logo.substring(0, 100) + '...' : 'null');
      }
      setTotalPages(response.pagination?.totalPages || 1);
      setTotalItems(response.pagination?.total || 0);
    } catch (err) {
      console.error('Error fetching favorites:', err);
      setError('Failed to load favorite suppliers');
      setFavoriteSuppliers([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, currentPage, itemsPerPage]);

  useEffect(() => {
    fetchFavorites();
  }, [fetchFavorites]);

  // Remove from favorites
  const handleRemoveFavorite = async (e, sellerId) => {
    e.stopPropagation();
    if (!user?.id) return;
    
    try {
      setRemovingId(sellerId);
      await favoritesApi.remove(user.id, sellerId);
      setFavoriteSuppliers(prev => prev.filter(s => s.id !== sellerId));
      setTotalItems(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error removing favorite:', err);
    } finally {
      setRemovingId(null);
    }
  };

  // Navigate to supplier profile
  const handleViewSupplier = (supplier) => {
    navigate(`/seller/${supplier.slug || supplier.id}`);
  };

  // Supplier Card Component - Matching Reference Design
  const SupplierCard = ({ supplier }) => {
    const isRemoving = removingId === supplier.id;
    const displayName = supplier.name || supplier.companyName || 'Unknown Supplier';
    const memberYears = supplier.memberYears || 1;
    const reviews = supplier.reviews || 0;
    const products = supplier.products || supplier._count?.products || 0;
    
    // Default fallback image
    const fallbackCover = 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600';
    
    // Check if coverImage is valid
    const getCoverImage = () => {
      if (!supplier.coverImage) return fallbackCover;
      // If it's a URL (http/https), use it
      if (supplier.coverImage.startsWith('http')) return supplier.coverImage;
      // If it's a data URI, use it
      if (supplier.coverImage.startsWith('data:')) return supplier.coverImage;
      return fallbackCover;
    };
    
    const coverImage = getCoverImage();
    
    return (
      <div className="fav-supplier-card" onClick={() => handleViewSupplier(supplier)}>
        {/* Cover Image Section */}
        <div className="fav-card-image-section">
          <img 
            src={coverImage} 
            alt="Cover" 
            className="fav-cover-image"
            onError={(e) => { 
              console.log('Cover image failed to load:', supplier.coverImage?.substring(0, 50));
              e.target.src = fallbackCover; 
            }}
          />
          {/* Bookmark Button */}
          <button 
            className={`fav-bookmark-btn ${isRemoving ? 'removing' : ''}`}
            onClick={(e) => handleRemoveFavorite(e, supplier.id)}
            disabled={isRemoving}
            title="Remove from favorites"
          >
            <svg width="14" height="19" viewBox="0 0 14 19" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 0C12.3978 0 12.7792 0.158149 13.0605 0.439453C13.3419 0.720758 13.5 1.10218 13.5 1.5V18C13.4998 18.1337 13.4634 18.2647 13.3955 18.3799C13.3276 18.495 13.2302 18.5905 13.1133 18.6553C12.9964 18.72 12.864 18.7524 12.7305 18.7487C12.5969 18.745 12.4664 18.7055 12.3535 18.6347L6.75 14.9297L1.14648 18.6347C1.03357 18.7055 0.90306 18.745 0.769531 18.7487C0.636003 18.7524 0.503552 18.72 0.38665 18.6553C0.269749 18.5905 0.172363 18.495 0.104492 18.3799C0.0366211 18.2647 0.000366211 18.1337 0 18V1.5C0 1.10218 0.158149 0.720758 0.439453 0.439453C0.720758 0.158149 1.10218 0 1.5 0H12Z" fill="#1F396C"/>
            </svg>
          </button>
        </div>

        {/* Card Content */}
        <div className="fav-card-content">
          {/* Logo + Company Name - side by side */}
          <div className="fav-company-info">
            <div className="fav-logo-wrapper">
              {supplier.logo ? (
                <img 
                  src={supplier.logo} 
                  alt={displayName} 
                  className="fav-logo-img"
                  onError={(e) => { 
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `<span class="fav-logo-letter">${displayName.charAt(0)}</span>`;
                  }}
                />
              ) : (
                <span className="fav-logo-letter">{displayName.charAt(0)}</span>
              )}
            </div>
            <div className="fav-company-name-container">
              <span className="fav-company-name">{displayName}</span>
            </div>
          </div>

          {/* Badges Row */}
          <div className="fav-badges-section">
            <div className="fav-badges-row">
              {(() => {
                const planConfig = getPlanConfig(supplier.membershipPlan);
                return (
                  <div className="fav-global-badge" style={{ borderColor: planConfig.color }}>
                    <img 
                      src={planConfig.icon} 
                      alt={planConfig.label} 
                      className="fav-global-icon"
                    />
                    <span style={{ color: planConfig.color }}>{planConfig.label}</span>
                  </div>
                );
              })()}
              {supplier.isVerified && (
                <div className="fav-verified-badge">
                  <svg width="16" height="16" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M11 0C11.9337 0 12.6871 1.26712 13.5472 1.49659C14.4377 1.73472 15.7221 1.02178 16.5014 1.47206C17.2923 1.92955 17.314 3.40305 17.9562 4.04383C18.5984 4.68461 20.0705 4.7077 20.528 5.49857C20.9782 6.27789 20.2653 7.56231 20.5034 8.45276C20.7329 9.31291 22 10.0663 22 11C22 11.9337 20.7329 12.6871 20.5034 13.5472C20.2653 14.4377 20.9782 15.7221 20.528 16.5015C20.0705 17.2923 18.597 17.314 17.9562 17.9562C17.314 18.5984 17.2923 20.0705 16.5014 20.528C15.7221 20.9782 14.4377 20.2653 13.5472 20.5034C12.6871 20.7329 11.9337 22 11 22C10.0663 22 9.31291 20.7329 8.45276 20.5034C7.56231 20.2653 6.27788 20.9782 5.49856 20.528C4.70769 20.0705 4.68605 18.597 4.04382 17.9562C3.4016 17.3154 1.92954 17.2923 1.47205 16.5015C1.02177 15.7221 1.73472 14.4377 1.49659 13.5472C1.26712 12.6871 0 11.9338 0 11C0 10.0663 1.26712 9.31291 1.49659 8.45276C1.73472 7.56231 1.02177 6.27789 1.47205 5.49857C1.92954 4.7077 3.40305 4.68605 4.04382 4.04383C4.68605 3.40161 4.70769 1.92955 5.49856 1.47206C6.27788 1.02178 7.56231 1.73472 8.45276 1.49659C9.31291 1.26712 10.0663 0 11 0Z" fill="#29ABE2"/>
                    <path d="M15.5876 7.60151C15.1229 7.13681 14.3696 7.13681 13.9049 7.60151L9.03125 12.4752L7.00786 10.4518C6.54315 9.98712 5.7898 9.98712 5.32509 10.4518C4.86039 10.9165 4.86039 11.6699 5.32509 12.1346L8.14798 14.9575C8.39188 15.2014 8.71372 15.3168 9.03272 15.3053C9.35164 15.3168 9.67341 15.2014 9.91731 14.9575L15.5891 9.28578C16.0538 8.82107 16.0538 8.06772 15.5891 7.60302L15.5876 7.60151Z" fill="white"/>
                  </svg>
                  <span>Verified</span>
                </div>
              )}
            </div>

            {/* Location Row */}
            <div className="fav-location-row">
              <div className="fav-flag-wrapper">
                <img 
                  src={supplier.countryFlag || "https://flagcdn.com/w80/ae.png"} 
                  alt="Flag" 
                  className="fav-flag-img"
                />
              </div>
              <span className="fav-location-text">
                <span className="fav-country">{supplier.country || 'UAE'} / </span>
                <span className="fav-years">{memberYears} Year{memberYears > 1 ? 's' : ''}</span>
              </span>
            </div>

            {/* Business Type */}
            <div className="fav-business-type">{supplier.businessType || 'Manufacturer'}</div>
          </div>

          {/* Stats Row */}
          <div className="fav-stats-row">
            <div className="fav-stat">
              <span className="fav-stat-value">{reviews} Reviews</span>
            </div>
            <div className="fav-stat">
              <span className="fav-stat-value">{products} Products</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Empty State Component
  const EmptyState = () => (
    <div className="fav-empty-state">
      <div className="fav-empty-icon">
        <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M17 3H7C5.9 3 5 3.9 5 5V21L12 18L19 21V5C19 3.9 18.1 3 17 3Z" fill="#E4E4E7"/>
        </svg>
      </div>
      <h3 className="fav-empty-title">No Favorite Suppliers Yet</h3>
      <p className="fav-empty-description">
        Start exploring suppliers and save your favorites for quick access.
        Click the bookmark icon on any supplier card to add them here.
      </p>
      <button className="fav-explore-btn" onClick={() => navigate('/top-sellers')}>
        Explore Suppliers
      </button>
    </div>
  );

  // Loading Skeleton
  const LoadingSkeleton = () => (
    <div className="fav-suppliers-grid">
      {Array(8).fill(null).map((_, index) => (
        <div key={index} className="fav-supplier-card fav-skeleton">
          <div className="fav-card-image-section fav-skeleton-bg"></div>
          <div className="fav-card-content">
            <div className="fav-company-info">
              <div className="fav-logo-wrapper fav-skeleton-circle"></div>
              <div className="fav-skeleton-text fav-skeleton-name"></div>
            </div>
            <div className="fav-skeleton-text fav-skeleton-badges"></div>
            <div className="fav-skeleton-text fav-skeleton-location"></div>
            <div className="fav-skeleton-text fav-skeleton-stats"></div>
          </div>
        </div>
      ))}
    </div>
  );

  // Pagination numbers
  const renderPaginationNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <span
          key={i}
          className={`fav-page-number ${currentPage === i ? 'active' : ''}`}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </span>
      );
    }
    return pages;
  };

  return (
    <SellerDashboardLayout>
      <div className="fav-page-content">
        {/* Page Header */}
        <div className="fav-content-header">
          <div className="fav-page-title-section">
            <h1 className="fav-page-title">Favorites Supplier</h1>
            <p className="fav-page-subtitle">
              Easily reach out your favorite supplier by saving their card.
            </p>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="fav-error-message">
            <span>{error}</span>
            <button onClick={fetchFavorites}>Try Again</button>
          </div>
        )}

        {/* Content */}
        {loading ? (
          <LoadingSkeleton />
        ) : favoriteSuppliers.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {/* Suppliers Grid */}
            <div className="fav-suppliers-grid">
              {favoriteSuppliers.map((supplier) => (
                <SupplierCard key={supplier.id} supplier={supplier} />
              ))}
            </div>

            {/* Pagination */}
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
          </>
        )}
      </div>
    </SellerDashboardLayout>
  );
};

export default FavoritesSupplierPage;
